/**
 * Chapter001.js — Document Control (OHSE 001)
 * Migrated from SWMS Platform Chapter001.tsx → JavaScript
 *
 * Props:
 *   swmsData  {object}  full swmsData — reads doc_* fields, revision_history, distribution_list
 *   onChange  {fn}      (field, value) => void — SWMSTab.handleChange
 *   companyName {string} from companyInfo.name
 */

import { useState } from 'react';
import { Plus, Pencil, Trash2, Check } from 'lucide-react';

const inputCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';
const thCls = 'px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide';

export default function Chapter001({ swmsData = {}, onChange, companyName }) {
  const [editingRevIdx, setEditingRevIdx] = useState(null);
  const [editingDistIdx, setEditingDistIdx] = useState(null);

  const revisions = swmsData.revision_history || [];
  const distribution = swmsData.distribution_list || [];
  const controller = companyName || '[Company Name]';

  // ── Doc info field helpers ──
  const setField = (field, value) => onChange(field, value);

  // ── Revision history ──
  const addRevision = () => {
    const next = [...revisions, { version: '', date: '', description: '', author: '', approved_by: '' }];
    onChange('revision_history', next);
    setEditingRevIdx(next.length - 1);
  };

  const updateRev = (idx, field, value) => {
    const next = [...revisions];
    next[idx] = { ...next[idx], [field]: value };
    onChange('revision_history', next);
  };

  const deleteRev = (idx) => {
    onChange('revision_history', revisions.filter((_, i) => i !== idx));
    if (editingRevIdx === idx) setEditingRevIdx(null);
  };

  // ── Distribution list ──
  const addDist = () => {
    const next = [...distribution, { recipient_name: '', organisation: '', copy_type: 'Controlled', date_issued: '' }];
    onChange('distribution_list', next);
    setEditingDistIdx(next.length - 1);
  };

  const updateDist = (idx, field, value) => {
    const next = [...distribution];
    next[idx] = { ...next[idx], [field]: value };
    onChange('distribution_list', next);
  };

  const deleteDist = (idx) => {
    onChange('distribution_list', distribution.filter((_, i) => i !== idx));
    if (editingDistIdx === idx) setEditingDistIdx(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 001</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">Document Control</h2>
      </div>

      {/* Section A: Document Information */}
      <Card title="Document Information" desc="Version tracking and review schedule for this OHSE Management Plan">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Version *">
            <input type="text" value={swmsData.doc_version || ''} placeholder="e.g. 1.0"
              onChange={e => setField('doc_version', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Review Date *">
            <input type="date" value={swmsData.doc_review_date || ''}
              onChange={e => setField('doc_review_date', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Next Review Date *">
            <input type="date" value={swmsData.doc_next_review || ''}
              onChange={e => setField('doc_next_review', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Author *">
            <input type="text" value={swmsData.doc_author || ''} placeholder="Document author"
              onChange={e => setField('doc_author', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Approver *">
            <input type="text" value={swmsData.doc_approver || ''} placeholder="Approved by"
              onChange={e => setField('doc_approver', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Approval Date *">
            <input type="date" value={swmsData.doc_approval_date || ''}
              onChange={e => setField('doc_approval_date', e.target.value)} className={inputCls} />
          </Field>
        </div>

        {/* Document Controller responsibilities */}
        {controller && (
          <div className="mt-5 pt-5 border-t border-[#e0dbd0]">
            <p className="text-sm font-medium text-[#1a1a2e] mb-3">
              <span className="font-semibold text-[#c9a84c]">{controller}</span> agrees to the following responsibilities:
            </p>
            <ul className="space-y-2">
              {[
                'Maintains an up to date version of this OHSE Management Plan.',
                'Retains all obsolete pages of the Plan for a minimum of 7 years.',
                'Provides a copy of the current version of the Plan to Principal Contractor.',
                'Reviews the Plan on a 3 months basis.',
                'Ensures all amendments to the Plan are recorded in the Register of Amendments.',
              ].map((text, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#1a1a2e]">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#c9a84c] shrink-0" />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {/* Section B: Revision History */}
      <Card title="Revision History" desc="Record all amendments to this document">
        {revisions.length === 0 ? (
          <EmptyAdd label="Add Revision" onAdd={addRevision} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-[#f0ede6]">
                    <th className={thCls}>Version</th>
                    <th className={thCls}>Date</th>
                    <th className={thCls}>Description</th>
                    <th className={thCls}>Author</th>
                    <th className={thCls}>Approved By</th>
                    <th className={thCls + ' text-right'}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {revisions.map((rev, idx) => (
                    <tr key={idx} className="border-b border-[#e0dbd0] hover:bg-[#f8f7f4]">
                      {editingRevIdx === idx ? (
                        <>
                          <td className="px-3 py-2"><input type="text" value={rev.version || ''} onChange={e => updateRev(idx, 'version', e.target.value)} className={inputCls} /></td>
                          <td className="px-3 py-2"><input type="date" value={rev.date || ''} onChange={e => updateRev(idx, 'date', e.target.value)} className={inputCls} /></td>
                          <td className="px-3 py-2"><input type="text" value={rev.description || ''} onChange={e => updateRev(idx, 'description', e.target.value)} className={inputCls} /></td>
                          <td className="px-3 py-2"><input type="text" value={rev.author || ''} onChange={e => updateRev(idx, 'author', e.target.value)} className={inputCls} /></td>
                          <td className="px-3 py-2"><input type="text" value={rev.approved_by || ''} onChange={e => updateRev(idx, 'approved_by', e.target.value)} className={inputCls} /></td>
                          <td className="px-3 py-2 text-right">
                            <button onClick={() => setEditingRevIdx(null)} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="w-4 h-4" /></button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-2 font-medium">{rev.version || '—'}</td>
                          <td className="px-3 py-2 text-gray-500">{rev.date || '—'}</td>
                          <td className="px-3 py-2">{rev.description || '—'}</td>
                          <td className="px-3 py-2">{rev.author || '—'}</td>
                          <td className="px-3 py-2">{rev.approved_by || '—'}</td>
                          <td className="px-3 py-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => setEditingRevIdx(idx)} className="p-1 text-[#c9a84c] hover:bg-[rgba(201,168,76,0.15)] rounded"><Pencil className="w-3.5 h-3.5" /></button>
                              <button onClick={() => deleteRev(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4">
              <GhostBtn onClick={addRevision} label="Add Revision" />
            </div>
          </>
        )}
      </Card>

      {/* Section C: Distribution List */}
      <Card title="Distribution List" desc="Controlled and uncontrolled copy recipients">
        {distribution.length === 0 ? (
          <EmptyAdd label="Add Recipient" onAdd={addDist} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-[#f0ede6]">
                    <th className={thCls}>Recipient</th>
                    <th className={thCls}>Organisation</th>
                    <th className={thCls}>Copy Type</th>
                    <th className={thCls}>Date Issued</th>
                    <th className={thCls + ' text-right'}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {distribution.map((dist, idx) => (
                    <tr key={idx} className="border-b border-[#e0dbd0] hover:bg-[#f8f7f4]">
                      {editingDistIdx === idx ? (
                        <>
                          <td className="px-3 py-2"><input type="text" value={dist.recipient_name || ''} onChange={e => updateDist(idx, 'recipient_name', e.target.value)} className={inputCls} /></td>
                          <td className="px-3 py-2"><input type="text" value={dist.organisation || ''} onChange={e => updateDist(idx, 'organisation', e.target.value)} className={inputCls} /></td>
                          <td className="px-3 py-2">
                            <select value={dist.copy_type || 'Controlled'} onChange={e => updateDist(idx, 'copy_type', e.target.value)}
                              className="w-full px-2 py-1.5 text-sm bg-white border border-[#e0dbd0] rounded text-[#1a1a2e] focus:outline-none focus:border-[#c9a84c]">
                              <option value="Controlled">Controlled</option>
                              <option value="Uncontrolled">Uncontrolled</option>
                            </select>
                          </td>
                          <td className="px-3 py-2"><input type="date" value={dist.date_issued || ''} onChange={e => updateDist(idx, 'date_issued', e.target.value)} className={inputCls} /></td>
                          <td className="px-3 py-2 text-right">
                            <button onClick={() => setEditingDistIdx(null)} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="w-4 h-4" /></button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-2 font-medium">{dist.recipient_name || '—'}</td>
                          <td className="px-3 py-2">{dist.organisation || '—'}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                              dist.copy_type === 'Controlled'
                                ? 'bg-[rgba(201,168,76,0.15)] text-[#1a1a2e]'
                                : 'bg-[#f0ede6] text-gray-600'
                            }`}>{dist.copy_type || 'Controlled'}</span>
                          </td>
                          <td className="px-3 py-2 text-gray-500">{dist.date_issued || '—'}</td>
                          <td className="px-3 py-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => setEditingDistIdx(idx)} className="p-1 text-[#c9a84c] hover:bg-[rgba(201,168,76,0.15)] rounded"><Pencil className="w-3.5 h-3.5" /></button>
                              <button onClick={() => deleteDist(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4">
              <GhostBtn onClick={addDist} label="Add Recipient" />
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

// ── Local sub-components ──────────────────────────────────────────────────────

function Card({ title, desc, children }) {
  return (
    <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
      <div className="px-4 py-2.5 bg-[#f0ede6]">
        <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">{title}</h3>
        {desc && <p className="text-xs text-gray-500 mt-0.5">{desc}</p>}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#1a1a2e] mb-1">{label}</label>
      {children}
    </div>
  );
}

function EmptyAdd({ label, onAdd }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <p className="text-sm text-gray-400 mb-3">No entries yet.</p>
      <GhostBtn onClick={onAdd} label={label} />
    </div>
  );
}

function GhostBtn({ onClick, label }) {
  return (
    <button type="button" onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] border border-[#c9a84c]/40 transition-colors">
      <Plus className="w-4 h-4" /> {label}
    </button>
  );
}

